let apiToken: string | undefined;
let deviceId: string | undefined;

// Built into mobile bundles so even the first request carries the version.
const appVersion = import.meta.env.VITE_APP_VERSION;

export function setApiToken(token: string | undefined) {
    apiToken = token;
}

// Apps identify themselves by their install ID.
export function setDeviceId(id: string) {
    deviceId = id;
}

// Browsers keep a random ID instead, so the devices an account is used on can
// be counted. Clearing it only adds a device, so it isn't worth guarding.
function browserDeviceId() {
    try {
        let id = localStorage.getItem('deviceId');
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem('deviceId', id);
        }
        return id;
    } catch {
        // Blank rather than undefined, so blocked storage isn't retried.
        return '';
    }
}

export function applyRequestHeaders(headers: Headers) {
    headers.set('X-Requested-With', 'XMLHttpRequest');

    if (appVersion) {
        headers.set('X-eMedici-Version', appVersion);
    } else {
        // Apps never fall back to a browser ID while their install ID loads,
        // which would count the app twice.
        deviceId ??= browserDeviceId();
    }

    if (deviceId) {
        headers.set('X-Device-Id', deviceId);
    }

    const xsrfToken = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)?.[1];
    if (xsrfToken) {
        headers.set('X-XSRF-TOKEN', decodeURIComponent(xsrfToken));
    }

    if (apiToken) {
        try {
            headers.set('Authorization', `Bearer ${apiToken}`);
        } catch {
            // Allow the request to continue so an invalid stored token cannot strand the app.
        }
    }

    return headers;
}

export function responseErrorMessage(response: Response | undefined, data: any) {
    if (response?.status === 429) {
        return data?.errors?.[0]?.detail || `You're going a bit too fast! Try again in a minute.`;
    }

    if (Array.isArray(data?.errors)) {
        return data.errors.map((error: any) => error.detail).join('<br>');
    }

    if (data?.errors) {
        return Object.values(data.errors)
            .flatMap((value) => value)
            .join('<br>');
    }

    return data?.message;
}
